import { Check, ShoppingCartIcon } from "lucide-react";
import { Button, buttonVariants } from "~/components/ui/button";
import { DefaultFormModal } from "~/components/DefaultForm/DefaultFormModal";
import { Link } from "~/components/Link";
import { CheckoutForm } from "~/modules/billing/components/CheckoutForm";
import { PlanTypes } from "~/modules/user/store/types";
import { Routes } from "~/routes";
import { BillingUserEvents } from "~/modules/analytics/events/billing";
import { cn } from "~/lib/utils";
import { PLAN_METADATA } from "../../consts";
import { PlanMetadata, PlanMetadataTypes } from "../../types";

// // // //

// Shared class name for CTA buttons
const CTA_CLASS_NAME =
    "h-12 w-full whitespace-normal font-medium text-base sm:text-sm";

// // // //

/**
 * PlanCard component
 * Renders a plan card with the plan metadata and children
 * @param props.plan - The plan metadata
 * @param props.children - The children to render inside the plan card
 */
function PlanCard(props: {
    plan: PlanMetadata;
    hasProPlan: boolean;
    hasEnterprisePlan: boolean;
    children: React.ReactNode;
}) {
    const { plan, hasProPlan, hasEnterprisePlan, children } = props;

    const proPlanRecommended =
        plan.type === PlanMetadataTypes.PRO &&
        !hasProPlan &&
        !hasEnterprisePlan;

    const matchesCurrentPlan =
        (plan.type === PlanMetadataTypes.PRO && hasProPlan) ||
        (plan.type === PlanMetadataTypes.ENTERPRISE && hasEnterprisePlan);

    return (
        <section
            key={plan.name}
            aria-label={`${plan.name} plan`}
            className={cn(
                "relative flex min-w-0 flex-col justify-between gap-6 rounded-xl border border-foreground/10 bg-card p-6",
                { "border-primary": proPlanRecommended || matchesCurrentPlan },
            )}
        >
            {/* Show "Recommended" badge if the plan is the recommended plan */}
            {proPlanRecommended && (
                <span className="absolute -top-3 left-6 rounded border border-primary-dark/10 bg-primary-light px-2 py-0.5 text-xs font-normal text-primary-dark">
                    Recommended
                </span>
            )}

            {/* Show "Current plan" badge if the plan matches the current plan */}
            {matchesCurrentPlan && (
                <span className="absolute -top-3 left-6 rounded border border-primary-dark/10 bg-primary-light px-2 py-0.5 text-xs font-normal text-primary-dark">
                    Current plan
                </span>
            )}
            <div>
                <h2 className="text-xl font-semibold text-balance">
                    {plan.name}
                </h2>
                <p className="mt-2 text-base text-pretty text-muted-foreground sm:text-sm @4xl:min-h-10">
                    {plan.purpose}
                </p>
                <div className="mt-6 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <p className="text-4xl font-semibold tracking-tight tabular-nums">
                        {plan.price}
                    </p>
                    {plan.period && (
                        <p className="text-base text-muted-foreground sm:text-sm">
                            {plan.period}
                        </p>
                    )}
                </div>
                <ul
                    role="list"
                    className="mt-6 space-y-3 border-t border-foreground/10 pt-6"
                >
                    {plan.features.map((feature) => (
                        <li
                            key={feature}
                            className="flex items-start gap-2 text-base sm:text-sm"
                        >
                            <Check
                                className="size-4 h-lh shrink-0 stroke-primary"
                                aria-hidden="true"
                            />
                            <span>{feature}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div>{children}</div>
        </section>
    );
}

// // // //

/**
 * ManagePlanLink component
 * Renders a link to the manage plan page for the given plan type
 * @param props.planType - The plan type
 */
function ManagePlanLink(props: { planType: PlanMetadataTypes }) {
    const { planType } = props;

    // Swap the Link.props.analyticsLabel based on the plan type
    const analyticsLabel =
        planType === PlanMetadataTypes.PRO
            ? BillingUserEvents.ProPlanButtonManagePlanLinkClick
            : BillingUserEvents.EnterprisePlanButtonManagePlanLinkClick;

    return (
        <Link
            href={Routes.settingsPlan}
            analyticsLabel={analyticsLabel}
            className={buttonVariants({
                variant: "outline",
                className: "h-12 w-full font-medium text-base sm:text-sm",
            })}
        >
            Manage plan
        </Link>
    );
}

// // // //

/**
 * PreviewPlanCta component
 * Renders a link to the preview plan page for the given plan type
 * @param props.isLoggedIn - Whether the user is logged in
 */
function PreviewPlanCta(props: { isLoggedIn: boolean }) {
    const { isLoggedIn } = props;

    const href = isLoggedIn ? Routes.assetScreener : Routes.login;

    const buttonText = isLoggedIn ? "Explore the platform" : "Login to explore";

    const analyticsLabel = isLoggedIn
        ? BillingUserEvents.PricingPageLoginLinkClick
        : BillingUserEvents.PricingPagePreviewPlanLinkClick;

    return (
        <Link
            href={href}
            analyticsLabel={analyticsLabel}
            className={buttonVariants({
                variant: "outline",
                className: "h-12 w-full font-medium text-base sm:text-sm",
            })}
        >
            {buttonText}
        </Link>
    );
}

// // // //

/**
 * ProPlanCta component
 * Renders a link to the pro plan page for the given plan type
 * @param props.isLoggedIn - Whether the user is logged in
 * @param props.hasProPlan - Whether the user has a pro plan
 * @param props.hasEnterprisePlan - Whether the user has an enterprise plan
 * @param props.isOwnerOrAdmin - Whether the user is an owner or admin
 * @param props.organizationID - The organization ID
 */
function ProPlanCta(props: {
    isLoggedIn: boolean;
    hasProPlan: boolean;
    hasEnterprisePlan: boolean;
    isOwnerOrAdmin: boolean;
    organizationID: string;
}) {
    const {
        isLoggedIn,
        hasProPlan,
        hasEnterprisePlan,
        isOwnerOrAdmin,
        organizationID,
    } = props;

    // Use not logged in -> show "Login to upgrade" link
    if (!isLoggedIn) {
        return (
            <Link
                href={Routes.login}
                analyticsLabel={BillingUserEvents.PricingPageLoginLinkClick}
                className={buttonVariants({
                    className: CTA_CLASS_NAME,
                })}
            >
                Log in to upgrade
            </Link>
        );
    }

    // User has enterprise plan -> don't show anything
    if (hasEnterprisePlan) {
        return null;
    }

    // User has pro plan -> show "Manage plan" link
    if (hasProPlan) {
        return <ManagePlanLink planType={PlanMetadataTypes.PRO} />;
    }

    // Owner/Admin User does NOT have pro plan -> show "Upgrade to Pro" button
    if (isOwnerOrAdmin) {
        return (
            <CheckoutForm
                text="Upgrade to Pro"
                organizationId={organizationID}
                className={CTA_CLASS_NAME}
                enableAdditionalSeatsSelect={false}
                icon={<ShoppingCartIcon />}
            />
        );
    }

    // Non owner/admin user does NOT have pro plan -> show disabled "Contact your team owner" button
    return (
        <Button type="button" disabled className={CTA_CLASS_NAME}>
            Contact your team owner
        </Button>
    );
}

// // // //

/**
 * EnterprisePlanCta component
 * Renders a link to the enterprise plan page for the given plan type
 * @param props.hasEnterprisePlan - Whether the user has an enterprise plan
 */
function EnterprisePlanCta(props: { hasEnterprisePlan: boolean }) {
    const { hasEnterprisePlan } = props;

    // User has enterprise plan -> show "Manage plan" link
    if (hasEnterprisePlan) {
        return <ManagePlanLink planType={PlanMetadataTypes.ENTERPRISE} />;
    }

    // User does not have enterprise plan -> show "Contact sales" button
    return (
        <DefaultFormModal
            title="Talk to us about Enterprise"
            body="Tell us about your production API, data integration, or licensing requirements."
            buttonText="Contact sales"
            buttonClassName={buttonVariants({
                variant: "outline",
                className: "h-12 w-full font-medium text-base sm:text-sm",
            })}
            analyticsLabel={
                BillingUserEvents.PricingPageOpenEnterpriseContactModal
            }
        />
    );
}

// // // //

/**
 * PlanCards component
 * Renders the plan cards for the given user
 * @param props.isLoggedIn - Whether the user is logged in
 * @param props.planType - The plan type
 * @param props.isOwnerOrAdmin - Whether the user is an owner or admin
 * @param props.organizationID - The organization ID
 */
export function PlanCards(props: {
    isLoggedIn: boolean;
    planType: PlanTypes;
    isOwnerOrAdmin: boolean;
    organizationID: string;
}) {
    const { isLoggedIn, planType, isOwnerOrAdmin, organizationID } = props;

    // ENHANCEMENT - add helper function for this?
    const hasProPlan = [
        PlanTypes.PREMIUM_DATA_DOWNLOADS,
        PlanTypes.TRIAL_DATA_DOWNLOADS,
    ].includes(planType);

    // ENHANCEMENT - add helper function for this?
    const hasEnterprisePlan = [
        PlanTypes.PREMIUM_ALL_ACCESS,
        PlanTypes.TRIAL_ALL_ACCESS,
    ].includes(planType);

    return (
        <div className="@container">
            <div className="grid gap-5 @4xl:grid-cols-3">
                {PLAN_METADATA.map((plan) => (
                    <PlanCard
                        key={plan.name}
                        plan={plan}
                        hasProPlan={hasProPlan}
                        hasEnterprisePlan={hasEnterprisePlan}
                    >
                        {/* Render Link for "Preview" PlanCard */}
                        {plan.type === PlanMetadataTypes.PREVIEW &&
                            !hasProPlan &&
                            !hasEnterprisePlan && (
                                <PreviewPlanCta isLoggedIn={isLoggedIn} />
                            )}

                        {/* Render Link for "Pro" PlanCard */}
                        {plan.type === PlanMetadataTypes.PRO && (
                            <ProPlanCta
                                isLoggedIn={isLoggedIn}
                                hasProPlan={hasProPlan}
                                hasEnterprisePlan={hasEnterprisePlan}
                                isOwnerOrAdmin={isOwnerOrAdmin}
                                organizationID={organizationID}
                            />
                        )}

                        {/* Render Link for "Enterprise" PlanCard */}
                        {plan.type === PlanMetadataTypes.ENTERPRISE && (
                            <EnterprisePlanCta
                                hasEnterprisePlan={hasEnterprisePlan}
                            />
                        )}
                    </PlanCard>
                ))}
            </div>
        </div>
    );
}
